import React from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { MapPin, Building2, Globe, Linkedin, Mail } from "lucide-react";
import regoditLogo from "../assets/regoditLogo.webp";
import SocialLink from "./SocialLink";
import SectionLink from "./SectionLink";

// Custom X (Twitter) icon component
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const footerLinks = [
    { name: "Home", href: "#top" },
    { name: "Product", href: "#product" },
    { name: "Why Regodit?", href: "#why-regodit" },
    { name: "Frameworks", href: "#frameworks" },
    { name: "Testimonials", href: "#testimonials" },
  ];

  const socialLinks = [
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/solsphere/",
      icon: Linkedin,
    },
    {
      name: "Email",
      url: "mailto:connect@solsphere.ai",
      icon: Mail,
    },
    {
      name: "X (Twitter)",
      url: "https://x.com/SolsphereAI",
      icon: XIcon,
    },
  ];

  const locations = [
    {
      city: "Delaware",
      country: "USA",
      icon: Building2,
    },
    {
      city: "Bangalore",
      country: "India",
      icon: Globe,
    },
  ];

  return (
    <footer
      className="relative border-t border-white/10"
      style={{
        background:
          "linear-gradient(180deg, #0A0A23 0%, #1A1A3A 50%, #0F0F2B 100%)",
      }}
    >
      {/* Unified Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 20% 30%, rgba(120, 119, 198, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
              linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%)
            `,
            pointerEvents: "none",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              {/* Logo */}
              <div className="flex items-center space-x-2 mb-6 sm:mb-8">
                <img
                  src={regoditLogo}
                  alt="RegoDit Logo"
                  className="h-8 sm:h-10 w-auto"
                />
              </div>

              <p className="text-white/70 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base max-w-lg">
                AI-powered compliance automation that keeps your business
                audit-ready 24/7. Transform compliance from a burden into a
                competitive advantage.
              </p>
            </motion.div>
          </div>

          {/* Footer Links */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h4 className="text-white font-semibold mb-4 sm:mb-6 text-base sm:text-lg">
              Navigation
            </h4>
            <ul className="space-y-2 sm:space-y-3">
              {footerLinks.map((link, index) => (
                <li key={link.name}>
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: index * 0.05,
                      duration: 0.4,
                      ease: "easeOut",
                    }}
                    whileHover={{ x: 2 }}
                  >
                    <SectionLink
                      href={link.href}
                      className="text-white/60 hover:text-white transition-colors duration-300 text-xs sm:text-sm block py-1"
                    >
                      {link.name}
                    </SectionLink>
                  </motion.div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Locations */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h4 className="text-white font-semibold mb-4 sm:mb-6 text-base sm:text-lg">
              Our Locations
            </h4>
            <div className="space-y-3 sm:space-y-4">
              {locations.map((location, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-2 sm:space-x-3"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                  whileHover={{ x: 2 }}
                >
                  <location.icon className="w-3 sm:w-4 h-3 sm:h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-white/80 text-xs sm:text-sm font-medium">
                      {location.city}
                    </div>
                    <div className="text-white/50 text-xs">
                      {location.country}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Footer */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row justify-between items-center pt-6 sm:pt-8 border-t border-white/10"
        >
          <div className="flex items-center space-x-4 text-white/60 text-xs sm:text-sm mb-4 md:mb-0">
            <span>© 2026 Solsphere AI Inc. All rights reserved.</span>
            <span className="text-white/30">|</span>
            <Link
              to="/privacy-policy"
              className="hover:text-white transition-colors duration-300"
            >
              Privacy Policy
            </Link>
          </div>

          <div className="flex items-center space-x-4 sm:space-x-6">
            {socialLinks.map((social) => (
              <SocialLink
                key={social.name}
                name={social.name}
                url={social.url}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-300"
              >
                <social.icon className="w-5 h-5" />
              </SocialLink>
            ))}
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
